/**
 * Hand Analyzer home — `/analyzer`. Lists the hands the user has built; each
 * row launches the coach to grade the line.
 *
 * The loop: build a hand (free) → save (free) → analyze with the coach (paid).
 * Building + saving never gate; the analysis launch routes through
 * DrillLaunchLink so a walled free user hits the paywall in place.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import type { HandSummary } from "@/shared/models";
import { deleteHand, listHands } from "@/shared/api";
import { handAnalysisRoute } from "@/shared/chatRoute";
import { relativeTime } from "@/shared/format";
import { Button, buttonClass } from "@/shared/components/Button";
import { DrillLaunchLink } from "@/shared/components/DrillLaunchLink";
import { EmptyState } from "@/shared/components/EmptyState";
import { ModalOverlay } from "@/shared/components/ModalOverlay";
import { NavIcons, Plus, Sparkles, Trash2 } from "@/shared/components/icons";
import { PageContainer } from "@/shared/components/layout/PageContainer";
import { PageHeader } from "@/shared/components/layout/PageHeader";

export function AnalyzerHome() {
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState<HandSummary | null>(null);

  const { data: hands, isLoading, isError } = useQuery({
    queryKey: ["hands"],
    queryFn: () => listHands(),
    staleTime: 30_000,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteHand(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hands"] });
      setConfirmDelete(null);
    },
  });

  return (
    <PageContainer width="default">
      <PageHeader
        Icon={NavIcons.analyzer}
        title="Hand Analyzer"
        subtitle="Build a hand you played — the coach grades your line."
        action={
          <Link to="/analyzer/new" className={buttonClass({ variant: "primary", size: "sm" })}>
            <Plus size={16} strokeWidth={2.25} />
            Build a hand
          </Link>
        }
      />

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading your hands…</p>
      ) : isError ? (
        <p className="text-sm text-danger">Couldn't load your hands.</p>
      ) : !hands || hands.length === 0 ? (
        <EmptyState
          Icon={NavIcons.analyzer}
          title="Replay a hand that's been bugging you"
          message="Build the hand street by street — your cards, the board, who did what — and your coach grades the line and shows you the leak. Building is free; the coach grade is the paid part."
          action={
            <Link to="/analyzer/new" className={buttonClass({ variant: "primary" })}>
              <Plus size={16} strokeWidth={2.25} />
              Build your first hand
            </Link>
          }
        />
      ) : (
        <div className="space-y-2">
          {hands.map((h) => (
            <HandRow key={h.id} hand={h} onDelete={() => setConfirmDelete(h)} />
          ))}
        </div>
      )}

      {confirmDelete && (
        <ModalOverlay title="Delete this hand?" onClose={() => setConfirmDelete(null)}>
          <div className="space-y-4">
            <p className="text-sm text-text-muted">
              “{confirmDelete.title || confirmDelete.summary}” will be permanently removed.
              {confirmDelete.analyzed && " Its coach analysis won't be deleted."}
            </p>
            {deleteMutation.isError && (
              <p className="text-sm text-danger">Couldn't delete — try again.</p>
            )}
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setConfirmDelete(null)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                onClick={() => deleteMutation.mutate(confirmDelete.id)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </Button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </PageContainer>
  );
}

function HandRow({ hand, onDelete }: { hand: HandSummary; onDelete: () => void }) {
  return (
    <section className="rounded-2xl border border-border bg-surface p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-medium text-text">
              {hand.title || hand.summary}
            </h3>
            {hand.analyzed && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-medium text-accent">
                <Sparkles size={11} strokeWidth={2} />
                Analyzed
              </span>
            )}
          </div>
          {hand.title && (
            <p className="mt-0.5 truncate text-xs text-text-muted">{hand.summary}</p>
          )}
          <p className="mt-0.5 text-xs text-text-muted">{relativeTime(hand.createdAt)}</p>
        </div>
        <button
          type="button"
          onClick={onDelete}
          aria-label="Delete hand"
          className="-mr-1 -mt-1 shrink-0 rounded-lg p-1.5 text-text-muted hover:text-danger transition"
        >
          <Trash2 size={16} strokeWidth={1.75} />
        </button>
      </div>

      <DrillLaunchLink
        to={handAnalysisRoute(hand.id)}
        className={buttonClass({ variant: "primary", size: "sm", fullWidth: true })}
      >
        <Sparkles size={16} strokeWidth={2} />
        {hand.analyzed ? "Re-analyze with coach" : "Analyze with coach"}
      </DrillLaunchLink>
    </section>
  );
}
